import {
    RuleData,
    OrGroupState,
    RuleFormState,
    SavedRule,
    RuleFormErrors,
    ConditionRow,
    ApprovalFormState,
    ApprovalFormErrors,
    SubmissionEntry,
    ApprovalLevel,
    ApprovalDecision,
    CreateHandlerFormState,
    CreateHandlerFormErrors,
    ConfiguredEwsSignal
} from './types';
import { EwsRulesMetadataDto } from 'service/approvalWorkflow';
import { RULE_NAME_MAX_LENGTH, EWS_SIGNAL_NAME_MAX_LENGTH } from './constants';

const SCHEDULE_VALUE_PATTERN = /^(\d+(?:\.\d+)?)\s*(d|day|days|h|hr|hrs|m|min|mins|s|sec|secs)?$/i;

export const parseScheduleValue = (raw: string, defaultUnit: 'm' | 'h' | 'd' = 'm'): { num: string; unit: 'm' | 'h' | 'd' } => {
    if (!raw) return { num: '', unit: defaultUnit };
    const trimmed = String(raw).trim();
    const match = /^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)?$/.exec(trimmed);
    if (!match) return { num: trimmed.replace(/[^\d.]/g, ''), unit: defaultUnit };

    const num = match[1] || '';
    const rawUnit = (match[2] || '').toLowerCase();

    let unit: 'm' | 'h' | 'd' = defaultUnit;
    if (rawUnit.startsWith('d')) {
        unit = 'd';
    } else if (rawUnit.startsWith('h')) {
        unit = 'h';
    } else if (rawUnit.startsWith('m')) {
        unit = 'm';
    }

    return { num, unit };
};

export const formatScheduleValue = (num: string | number, unit: string = 'm'): string => {
    const trimmed = String(num).trim();
    if (!trimmed) return '';
    return `${trimmed}${unit}`;
};

export const emptyRule = (): RuleData => ({ field: '', component: '', value: '' });

export const emptyOrGroups = (): OrGroupState[] => [{ rules: [emptyRule()], expanded: true }];

export const emptyRuleForm = (): RuleFormState => ({
    ruleName: '',
    ewsSignalName: '',
    metricKPI: '',
    dataSource: '',
    riskLevel: '',
    detectionLogic: '',
    notes: ''
});

export const hasCompletePolicyRule = (orGroups: OrGroupState[]) =>
    orGroups.some((group) => group.rules.some((rule) => rule.field.trim() && rule.component && rule.value.trim()));

export const validateRuleForm = (form: RuleFormState, savedRules: SavedRule[], editingId: number | null): RuleFormErrors => {
    const errors: RuleFormErrors = {};
    const name = form.ruleName.trim();
    if (!name) {
        errors.ruleName = 'Rule Name is required.';
    } else if (name.length > RULE_NAME_MAX_LENGTH) {
        errors.ruleName = `Rule Name must be ${RULE_NAME_MAX_LENGTH} characters or fewer.`;
    } else if (savedRules.some((r) => r.id !== editingId && r.ruleName.toLowerCase() === name.toLowerCase())) {
        errors.ruleName = 'A rule with this name already exists.';
    }

    const signal = form.ewsSignalName.trim();
    if (!signal) {
        errors.ewsSignalName = 'EWS Signal selection is required.';
    }

    return errors;
};

export const getRiskColor = (risk: string) => {
    const normalized = (risk || '').toLowerCase();
    if (normalized === 'critical' || normalized === 'high') return '#e53935';
    if (normalized === 'medium') return '#ffb300';
    return '#4caf50';
};

export const slugify = (label: string) => label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const validateScheduleValue = (label: string, value: string): string | undefined => {
    const trimmed = (value || '').trim();
    if (!trimmed) return `${label} is required.`;

    const match = SCHEDULE_VALUE_PATTERN.exec(trimmed);
    if (!match) return `${label} must be a valid number and unit (e.g. "14d", "1h", "30m").`;

    const numericValue = Number(match[1]);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
        return `${label} must be greater than 0.`;
    }

    const rawUnit = (match[2] || 'm').toLowerCase();
    let totalMinutes = 0;
    if (rawUnit.startsWith('d')) {
        totalMinutes = numericValue * 1440;
    } else if (rawUnit.startsWith('h')) {
        totalMinutes = numericValue * 60;
    } else if (rawUnit.startsWith('m')) {
        totalMinutes = numericValue;
    } else if (rawUnit.startsWith('s')) {
        totalMinutes = numericValue / 60;
    }

    if (totalMinutes < 1) {
        return `${label} must be at least 1 minute (e.g. "5m", "1h").`;
    }

    if (totalMinutes > 129600) {
        return `${label} must not exceed 90 days.`;
    }

    return undefined;
};

const detectConditionField = (lowerCaseDetectionLogic: string): string => {
    if (lowerCaseDetectionLogic.includes('volume')) return 'volume';
    if (lowerCaseDetectionLogic.includes('latency')) return 'latency';
    return 'growth-rule';
};

export const normalizeOperator = (op?: string): string => {
    if (!op) return '';
    const upper = String(op).trim().toUpperCase();
    if (upper === 'GREATER_THAN' || upper === 'GT' || upper === '>') return '>';
    if (upper === 'LESS_THAN' || upper === 'LT' || upper === '<') return '<';
    if (upper === 'GREATER_THAN_OR_EQUAL' || upper === 'GTE' || upper === '>=') return '>=';
    if (upper === 'LESS_THAN_OR_EQUAL' || upper === 'LTE' || upper === '<=') return '<=';
    if (upper === 'EQUALS' || upper === 'EQUAL' || upper === 'EQ' || upper === '==') return '==';
    if (upper === 'NOT_EQUALS' || upper === 'NOT_EQUAL' || upper === 'NE' || upper === '!=') return '!=';
    return op;
};

export const deriveConditionsFromDetectionLogic = (detectionLogic: string): ConditionRow[] => {
    const emptyCondition: ConditionRow = { field: '', component: '', value: '', unit: '' };
    if (!detectionLogic.trim()) return [emptyCondition];

    const operator = ['>=', '<=', '==', '!=', '>', '<'].find((op) => detectionLogic.includes(op)) ?? '';
    const field = detectConditionField(detectionLogic.toLowerCase());
    const unit = detectionLogic.includes('%') ? '%' : 'ms';

    return [{ field, component: operator, value: '', unit }];
};

export const sanitizeEsQueryStructure = (queryStr: string = ''): string => {
    if (!queryStr || typeof queryStr !== 'string') return queryStr;
    let fixed = queryStr.trim();
    if (fixed.startsWith("'") && fixed.endsWith("'")) {
        fixed = fixed.slice(1, -1).trim();
    }

    fixed = fixed.replace(/(\}\]\s*\})\s*,\s*(\\?\"aggs\\?\")/g, '$1},$2');

    let openCount = 0;
    let inQuotes = false;
    let escape = false;
    for (let i = 0; i < fixed.length; i++) {
        const char = fixed[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (char === '\\') {
            escape = true;
            continue;
        }
        if (char === '"') {
            inQuotes = !inQuotes;
            continue;
        }
        if (!inQuotes) {
            if (char === '{') openCount++;
            else if (char === '}') openCount--;
        }
    }

    while (openCount < 0 && fixed.endsWith('}')) {
        fixed = fixed.slice(0, -1).trim();
        openCount++;
    }
    while (openCount > 0) {
        fixed = fixed + '}';
        openCount--;
    }

    return fixed;
};

export const balanceJsonBrackets = (jsonStr: string): string => {
    return sanitizeEsQueryStructure(jsonStr);
};

export const ensureQueryVariables = (
    queryStr: string = '',
    existingVariables: any[] = [],
    signalMetadataVariables: any[] = []
): any[] => {
    const varMap = new Map<string, any>();

    // 1. Seed with signal metadata variables
    if (Array.isArray(signalMetadataVariables)) {
        signalMetadataVariables.forEach((sv: any) => {
            const id = (sv?.variableIdentifier || sv?.variableName || sv?.key || '').toUpperCase();
            if (id) {
                varMap.set(id, {
                    variableName: sv.variableName || sv.variableIdentifier || sv.key || id,
                    variableIdentifier: sv.variableIdentifier || sv.variableName || sv.key || id,
                    value: sv.value,
                    ...(sv.operator ? { operator: sv.operator } : {})
                });
            }
        });
    }

    // 2. Override with configured/existing variables (from UI or saved rule)
    if (Array.isArray(existingVariables)) {
        existingVariables.forEach((ev: any) => {
            const id = (ev?.variableIdentifier || ev?.variableName || ev?.key || '').toUpperCase();
            if (id) {
                const prev = varMap.get(id) || {};
                varMap.set(id, {
                    variableName: ev.variableName || prev.variableName || ev.key || id,
                    variableIdentifier: ev.variableIdentifier || prev.variableIdentifier || ev.key || id,
                    value: ev.value !== undefined && ev.value !== '' ? ev.value : (prev.value ?? ''),
                    ...(ev.operator || prev.operator ? { operator: ev.operator || prev.operator } : {})
                });
            }
        });
    }

    // 3. Scan query string for any {{VARIABLE}} placeholders not yet present
    if (queryStr && typeof queryStr === 'string') {
        const matches = queryStr.matchAll(/\{\{([A-Za-z0-9_]+)\}\}/g);
        for (const m of matches) {
            const placeholder = m[1];
            const upper = placeholder.toUpperCase();

            // Ignore backend auto-generated variables
            if (
                upper === 'EVAL_WINDOW_START' ||
                upper === 'EVAL_WINDOW_END' ||
                upper === 'RESOURCE_IDENTIFIERS' ||
                upper.startsWith('RESOURCE_')
            ) {
                continue;
            }

            if (!varMap.has(upper)) {
                let defaultValue: any = 0;
                if (upper === 'TIME_FROM') defaultValue = 'now-14d/d';
                else if (upper === 'TIME_TO') defaultValue = 'now';
                else if (upper.includes('MULTIPLIER') || upper.includes('RATIO')) defaultValue = 1.2;
                else if (upper.includes('PERCENTILE') || upper === 'P95' || upper.includes('PERCENT')) defaultValue = 95;
                else if (upper.includes('THRESHOLD') || upper.includes('DURATION') || upper.includes('LATENCY')) defaultValue = 1800000;
                else if (upper.includes('COUNT') || upper.includes('SIZE')) defaultValue = 100;

                varMap.set(upper, {
                    variableName: placeholder.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                    variableIdentifier: placeholder,
                    value: defaultValue
                });
            }
        }
    }

    return Array.from(varMap.values());
};

export const mapDtoToEwsSignals = (dtos: EwsRulesMetadataDto[]): ConfiguredEwsSignal[] => {
    return dtos.map((dto, idx) => {
        const id = dto._id || `signal-${idx + 1}`;
        const signalName = dto.EWS_Signal_Name || '';
        const metricKPI = (dto.Metric_KPI || '').toLowerCase();
        const metricLabel = dto.Metric_KPI || 'Unknown Metric';
        const dataSource = (dto.Data_Source || 'source1').toLowerCase();
        const dataSourceLabel = dto.Data_Source || 'Ingestion Metrics';
        const riskLevel = (dto.Risk_Level || 'medium').toLowerCase();
        const detectionLogic = dto.Detection_Logic_Trend_Pattern || '';
        const domain = dto.Domain || '';
        const component = dto.Component || '';
        const layer = dto.Layer_Tool || '';

        let rawEs = dto.Base_ES_Query || (dto as any).Final_ES_Query;
        if (typeof rawEs === 'string') {
            try {
                let cleaned = rawEs.trim();
                if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
                    cleaned = cleaned.slice(1, -1).trim();
                }
                rawEs = JSON.parse(cleaned);
            } catch (e) {
                // keep as is
            }
        }

        let baseEsQuery: any = undefined;
        let baseVariables: any[] = [];
        if (rawEs && typeof rawEs === 'object') {
            let indexName = rawEs.indexName || '';
            if (typeof indexName === 'string') {
                indexName = indexName.replace(/^'|'$/g, '').replace(/^"|"$/g, '').trim();
            }
            let query = rawEs.query || '';
            if (typeof query === 'string') {
                query = balanceJsonBrackets(query.replace(/^'|'$/g, '').trim());
            }
            const vars = Array.isArray(rawEs.variables)
                ? rawEs.variables
                : (Array.isArray(rawEs.Variables) ? rawEs.Variables : []);

            const fallbackVars = Array.isArray((dto as any).variables)
                ? (dto as any).variables
                : (Array.isArray((dto as any).Variables) ? (dto as any).Variables : []);

            baseVariables = ensureQueryVariables(query, vars, fallbackVars);

            baseEsQuery = {
                indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                query,
                variables: baseVariables,
                Variables: baseVariables,
                keysToExtract: rawEs.keysToExtract || []
            };
        }

        const operator = ['>=', '<=', '==', '!=', '>', '<'].find((op) => detectionLogic.includes(op)) || '>';
        const valMatch = detectionLogic.match(/(\d+(?:\.\d+)?)/);
        const defaultValue = valMatch ? valMatch[1] : '50';
        const field = metricKPI || 'volume';

        return {
            id,
            signalName,
            label: signalName,
            metricKPI,
            metricLabel,
            dataSource,
            dataSourceLabel,
            riskLevel,
            detectionLogic,
            domain,
            component,
            layer,
            baseEsQuery,
            baseVariables: baseVariables.length > 0 ? baseVariables : undefined,
            queryConfig: {
                field,
                operator,
                defaultValue
            }
        };
    });
};

// --- Approval tab ---

export const validateApprovalForm = (form: ApprovalFormState): ApprovalFormErrors => {
    const errors: ApprovalFormErrors = {};
    if (!form.level1.approverEmail.trim()) errors.level1ApproverEmail = 'Approver Email/Group is required.';
    if (!form.level2.approverEmail.trim()) errors.level2ApproverEmail = 'Approver Email/Group is required.';
    return errors;
};

export const getApprovalStatusColor = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'ACTIVE') return { bg: '#e8f5e9', color: '#2e7d32' };
    if (s === 'APPROVED') return { bg: '#c8f7c5', color: '#2e7d32' };
    if (s === 'ARCHIVED') return { bg: '#ede7f6', color: '#5e35b1' };
    if (s === 'REJECTED') return { bg: '#fbd5d5', color: '#c62828' };
    if (s === 'CHANGES_REQUESTED') return { bg: 'rgba(57, 172, 231, 0.16)', color: '#0B4F73' };
    if (s === 'PENDING_REVIEW' || s === 'PENDING' || s === 'PENDING_APPROVAL' || s === 'READY_FOR_APPROVAL' || s === 'READY FOR APPROVAL') return { bg: '#fce8b2', color: '#f57f17' };
    if (s === 'DRAFT' || s === 'NOT_STARTED' || s === 'NOT STARTED') return { bg: '#f1f5f9', color: '#64748b' };
    return { bg: '#e0e0e0', color: '#616161' };
};

export const isLevelActionable = (submission: SubmissionEntry, level: ApprovalLevel): boolean => {
    if (submission.overallStatus !== 'PENDING_REVIEW' && submission.overallStatus !== 'PENDING' && submission.overallStatus !== 'PENDING_APPROVAL') return false;

    if (level === 1) {
        return submission.level1Status === 'PENDING' || submission.level1Status === 'DRAFT' || submission.level1Status === 'NOT_STARTED';
    }

    if (level === 2) {
        if (submission.level2Status === 'APPROVED' || submission.level2Status === 'REJECTED') return false;
        if (submission.approvalMode === 'parallel' || submission.approvalMode === 'PARALLEL') {
            return true;
        }
        // Sequential mode: Level 2 becomes actionable once Level 1 is APPROVED
        return submission.level1Status === 'APPROVED';
    }

    return false;
};

const SLA_HOURS_BY_VALUE: Record<string, number> = { '24h': 24, '48h': 48, '72h': 72 };

const parseSubmittedAt = (submittedAt: string): number => {
    const match = /^(\d{2})-(\d{2})-(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i.exec(submittedAt.trim());
    if (!match) return NaN;
    const [, day, month, year, hourStr, minute, second, meridiem] = match;
    let hour = Number(hourStr) % 12;
    if (meridiem.toUpperCase() === 'PM') hour += 12;
    return new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minute), Number(second)).getTime();
};

export const isSlaBreached = (submittedAt: string, slaValue: string, now: number = Date.now()): boolean => {
    const submittedTime = parseSubmittedAt(submittedAt);
    const slaHours = SLA_HOURS_BY_VALUE[slaValue];
    if (Number.isNaN(submittedTime) || !slaHours) return false;
    return now - submittedTime > slaHours * 60 * 60 * 1000;
};

export const applyApprovalDecision = (submission: SubmissionEntry, level: ApprovalLevel, decision: ApprovalDecision): SubmissionEntry => {
    if (decision === 'reject') {
        return {
            ...submission,
            overallStatus: 'REJECTED',
            level1Status: level === 1 ? 'REJECTED' : submission.level1Status,
            level2Status: level === 2 ? 'REJECTED' : submission.level2Status
        };
    }

    if (decision === 'request-changes') {
        return {
            ...submission,
            overallStatus: 'DRAFT',
            level1Status: 'PENDING',
            level2Status: submission.approvalMode === 'parallel' || submission.approvalMode === 'PARALLEL' ? 'PENDING' : 'DRAFT'
        };
    }

    const level1Status = level === 1 ? 'APPROVED' : submission.level1Status;
    let level2Status = submission.level2Status;

    if (level === 1 && (submission.approvalMode === 'sequential' || submission.approvalMode === 'SEQUENTIAL')) {
        if (level2Status !== 'APPROVED') {
            level2Status = 'PENDING';
        }
    } else if (level === 2) {
        level2Status = 'APPROVED';
    }

    const overallStatus = level1Status === 'APPROVED' && level2Status === 'APPROVED' ? 'APPROVED' : 'PENDING_REVIEW';

    return { ...submission, level1Status, level2Status, overallStatus };
};

export const validateCreateHandlerForm = (form: CreateHandlerFormState): CreateHandlerFormErrors => {
    const errors: CreateHandlerFormErrors = {};
    if (!form.handlerName.trim()) errors.handlerName = 'Handler Name is required.';
    if (!form.handlerType) errors.handlerType = 'Type is required.';
    return errors;
};

export const mapDtoToSavedRule = (dto: any, localId: number): SavedRule => {
    const approvalOverall = typeof dto.Approval_Id === 'object' ? dto.Approval_Id?.overallStatus : (dto.approvalStatus || dto.overallStatus);
    let rawStatus = 'DRAFT';
    if (dto.Is_Active) {
        rawStatus = 'ACTIVE';
    } else if (approvalOverall === 'REJECTED') {
        rawStatus = 'REJECTED';
    } else if (approvalOverall === 'PENDING' || approvalOverall === 'PENDING_APPROVAL' || approvalOverall === 'PENDING_REVIEW') {
        rawStatus = 'PENDING_APPROVAL';
    } else if (dto.Is_Approved || approvalOverall === 'APPROVED') {
        rawStatus = 'APPROVED';
    }
    const metric = dto.Metric_KPI || 'Unknown Metric';
    const metricKPI = (dto.Metric_KPI || '').toLowerCase();
    const dataSource = dto.Data_Source || 'Ingestion Metrics';
    const riskLevel = (dto.Risk_Level || 'MEDIUM').toLowerCase();
    const detectionLogic = dto.Detection_Logic_Trend_Pattern || '';
    const ruleName = dto.Rule_Name || dto.EWS_Signal_Name || `Rule-${localId}`;
    const ewsSignalName = dto.EWS_Signal_Name || '';
    const notes = dto.Notes || '';

    let rawEs = dto.Base_ES_Query || dto.Final_ES_Query;
    if (typeof rawEs === 'string') {
        try {
            let cleaned = rawEs.trim();
            if (cleaned.startsWith("'") && cleaned.endsWith("'")) {
                cleaned = cleaned.slice(1, -1).trim();
            }
            rawEs = JSON.parse(cleaned);
        } catch (e) {
            // keep
        }
    }

    let baseVariables: any[] = [];
    let parsedEsQuery: any = undefined;
    if (rawEs && typeof rawEs === 'object') {
        let indexName = rawEs.indexName || '';
        if (typeof indexName === 'string') {
            indexName = indexName.replace(/^'|'$/g, '').replace(/^"|"$/g, '').trim();
        }
        let query = rawEs.query || '';
        if (typeof query === 'string') {
            query = balanceJsonBrackets(query.replace(/^'|'$/g, '').trim());
        }
        const vars = Array.isArray(rawEs.variables)
            ? rawEs.variables
            : (Array.isArray(rawEs.Variables) ? rawEs.Variables : []);

        const fallbackVars = Array.isArray(dto.variables) ? dto.variables : (Array.isArray(dto.Variables) ? dto.Variables : []);

        baseVariables = ensureQueryVariables(query, vars, fallbackVars);

        parsedEsQuery = {
            indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
            query,
            variables: baseVariables,
            Variables: baseVariables,
            keysToExtract: rawEs.keysToExtract || []
        };
    }

    const conditions = deriveConditionsFromDetectionLogic(detectionLogic);
    const rules: RuleData[] = baseVariables.length > 0
        ? baseVariables.map((v: any) => ({
            field: v.variableName || v.variableIdentifier || v.key || metric || 'Threshold',
            component: normalizeOperator(v.operator || (typeof v.value === 'object' ? v.value?.operator : '')) || '>',
            value: v.value != null ? (typeof v.value === 'object' ? String(v.value?.threshold ?? v.value?.value ?? '') : String(v.value)) : '',
            variableIdentifier: v.variableIdentifier || v.variableName || v.key
        }))
        : conditions.map((c) => ({
            field: c.field || metricKPI || 'volume',
            component: c.component || '>',
            value: c.value || '50'
        }));

    const orGroups: OrGroupState[] = [
        {
            expanded: true,
            rules
        }
    ];

    return {
        id: localId,
        backendId: dto._id,
        ruleName,
        ewsSignalName,
        metric,
        metricKPI,
        dataSourceLabel: dataSource,
        dataSourceValue: dataSource,
        riskLevel,
        detectionLogic,
        notes,
        orGroups,
        version: 'v1.0',
        status: rawStatus as any,
        domain: dto.Domain || 'Volume',
        component: dto.Component || 'adf',
        layer: dto.Layer_Tool || 'bronze',
        baseEsQuery: parsedEsQuery || rawEs,
        variables: baseVariables.length > 0 ? baseVariables : (dto.variables || dto.Variables),
        approvalId: typeof dto.Approval_Id === 'object' ? dto.Approval_Id?._id : (dto.Approval_Id || null),
        workflowId: typeof dto.Workflow_Id === 'object' ? dto.Workflow_Id?._id : (dto.Workflow_Id || null),
        isMapped: !!((dto.Handler && String(dto.Handler).trim() && String(dto.Handler).trim() !== '—') || (dto.handler && String(dto.handler).trim() && String(dto.handler).trim() !== '—')),
        handler: dto.Handler || dto.handler || undefined,
        handlerType: dto.Handler_Type || dto.handlerType || undefined,
        testRunStatus: dto.Test_Run_Status || 'NOT_RUN',
        Test_Run_Status: dto.Test_Run_Status || 'NOT_RUN',
        resources: dto.Resources || dto.resources || []
    };
};

export const mapDtoListToSavedRules = (dtos: any[]): SavedRule[] => {
    return (dtos || []).map((dto, idx) => mapDtoToSavedRule(dto, idx + 1));
};
